// Sistema de validaciones de input mejoradas.
// Implementa validaciones estrictas en capa de servicio.
// Previene errores y mejora seguridad del sistema.
// Basado en implementación de Alex optimizada para el sistema actual.

/**
 * Utilidades de validación comunes.
 */
const validationHelpers = {
  /**
   * Valida que un string no esté vacío.
   * @param {string} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @returns {Array} Array de errores
   */
  validateNonEmptyString(value, fieldName) {
    const errors = [];
    
    if (value === null || value === undefined) {
      errors.push(`${fieldName} es requerido`);
    } else if (typeof value !== 'string') {
      errors.push(`${fieldName} debe ser un texto`);
    } else if (value.trim().length === 0) {
      errors.push(`${fieldName} no puede estar vacío`);
    }
    
    return errors;
  },

  /**
   * Valida longitud de un string.
   * @param {string} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @param {number} minLength - Longitud mínima
   * @param {number} maxLength - Longitud máxima
   * @returns {Array} Array de errores
   */
  validateStringLength(value, fieldName, minLength = 0, maxLength = Infinity) {
    const errors = [];
    
    if (typeof value !== 'string') {
      errors.push(`${fieldName} debe ser un texto`);
      return errors;
    }
    
    const length = value.trim().length;
    
    if (length < minLength) {
      errors.push(`${fieldName} debe tener al menos ${minLength} caracteres`);
    }
    
    if (length > maxLength) {
      errors.push(`${fieldName} no puede exceder ${maxLength} caracteres`);
    }
    
    return errors;
  },

  /**
   * Valida formato de UUID.
   * @param {string} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @returns {Array} Array de errores
   */
  validateUUID(value, fieldName) {
    const errors = [];
    
    if (typeof value !== 'string') {
      errors.push(`${fieldName} debe ser un texto`);
      return errors;
    }
    
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    
    if (!uuidRegex.test(value)) {
      errors.push(`${fieldName} debe tener un formato UUID válido`);
    }
    
    return errors;
  },

  /**
   * Valida que un valor sea un objeto.
   * @param {any} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @returns {Array} Array de errores
   */
  validateObject(value, fieldName) {
    const errors = [];
    
    if (value === null || value === undefined) {
      errors.push(`${fieldName} es requerido`);
    } else if (typeof value !== 'object' || Array.isArray(value)) {
      errors.push(`${fieldName} debe ser un objeto`);
    }
    
    return errors;
  }
};

/**
 * Validaciones específicas para el módulo de chat.
 */
export const chatValidations = {
  /**
   * Valida el objeto mensaje para enviar.
   * @param {Object} mensajito - Objeto mensaje a validar
   * @returns {Object} Resultado de validación
   */
  sendMessage(mensajito) {
    const errors = [];
    
    // Validar que mensajito sea un objeto
    const objectErrors = validationHelpers.validateObject(mensajito, 'mensajito');
    errors.push(...objectErrors);
    
    if (errors.length > 0) {
      return { isValid: false, errors };
    }
    
    // Validar destinatario
    const destinatarioErrors = validationHelpers.validateNonEmptyString(
      mensajito.destinatario, 
      'destinatario'
    );
    errors.push(...destinatarioErrors);
    
    // Validar que destinatario sea UUID
    if (mensajito.destinatario) {
      const uuidErrors = validationHelpers.validateUUID(
        mensajito.destinatario, 
        'destinatario'
      );
      errors.push(...uuidErrors);
    }
    
    // Validar body del mensaje
    const bodyErrors = validationHelpers.validateNonEmptyString(
      mensajito.body, 
      'body'
    );
    errors.push(...bodyErrors);
    
    // Validar longitud del body
    if (mensajito.body) {
      const lengthErrors = validationHelpers.validateStringLength(
        mensajito.body, 
        'body', 
        1, 
        300 // Límite según especificaciones
      );
      errors.push(...lengthErrors);
    }
    
    // Validar msg_type si existe
    if (mensajito.msg_type !== undefined) {
      const validTypes = [1, 2, 3, 4, 5, 6]; // Tipos válidos según sistema
      if (!validTypes.includes(mensajito.msg_type)) {
        errors.push(`msg_type debe ser uno de: ${validTypes.join(', ')}`);
      }
    }
    
    // Validar chatroom_id si existe
    if (mensajito.chatroom_id !== undefined) {
      const chatroomErrors = validationHelpers.validateUUID(
        mensajito.chatroom_id, 
        'chatroom_id'
      );
      errors.push(...chatroomErrors);
    }
    
    // Validar que no haya contenido malicioso
    if (mensajito.body) {
      const suspiciousPatterns = [
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        /javascript:/gi,
        /on\w+\s*=/gi
      ];
      
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(mensajito.body)) {
          errors.push('El mensaje contiene contenido no permitido');
          break;
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Valida parámetros para obtener mensajes de chatroom.
   * @param {string} friendId - ID del amigo
   * @returns {Object} Resultado de validación
   */
  getChatroomMessages(friendId) {
    const errors = [];
    
    // Validar friendId
    const idErrors = validationHelpers.validateNonEmptyString(friendId, 'friendId');
    errors.push(...idErrors);
    
    // Validar que friendId sea UUID
    if (friendId) {
      const uuidErrors = validationHelpers.validateUUID(friendId, 'friendId');
      errors.push(...uuidErrors);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Valida parámetros para obtener chatrooms.
   * @param {Object} session - Objeto de sesión
   * @returns {Object} Resultado de validación
   */
  getMyChatrooms(session) {
    const errors = [];
    
    // Validar que session sea un objeto
    const objectErrors = validationHelpers.validateObject(session, 'session');
    errors.push(...objectErrors);
    
    if (errors.length > 0) {
      return { isValid: false, errors };
    }
    
    // Validar que session.user exista
    if (!session.user) {
      errors.push('session.user es requerido');
    } else {
      // Validar que session.user.id sea UUID
      if (session.user.id) {
        const uuidErrors = validationHelpers.validateUUID(
          session.user.id, 
          'session.user.id'
        );
        errors.push(...uuidErrors);
      } else {
        errors.push('session.user.id es requerido');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Valida parámetros para marcar mensaje como leído.
   * @param {string} messageId - ID del mensaje
   * @returns {Object} Resultado de validación
   */
  markMessageAsRead(messageId) {
    const errors = [];
    
    // Validar messageId
    const idErrors = validationHelpers.validateNonEmptyString(messageId, 'messageId');
    errors.push(...idErrors);
    
    // Validar que messageId sea UUID
    if (messageId) {
      const uuidErrors = validationHelpers.validateUUID(messageId, 'messageId');
      errors.push(...uuidErrors);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Valida parámetros para eliminar chatroom.
   * @param {Object} session - Objeto de sesión
   * @param {string} chatroomId - ID del chatroom
   * @returns {Object} Resultado de validación
   */
  deleteChatroom(session, chatroomId) {
    const errors = [];
    
    // Validar sesión
    const sessionErrors = this.getMyChatrooms(session);
    errors.push(...sessionErrors.errors);
    
    // Validar chatroomId
    const idErrors = validationHelpers.validateNonEmptyString(chatroomId, 'chatroomId');
    errors.push(...idErrors);
    
    // Validar que chatroomId sea UUID
    if (chatroomId) {
      const uuidErrors = validationHelpers.validateUUID(chatroomId, 'chatroomId');
      errors.push(...uuidErrors);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Valida parámetros para paginación de mensajes.
   * @param {string} friendId - ID del amigo
   * @param {number} page - Página solicitada
   * @param {number} pageSize - Tamaño de página
   * @returns {Object} Resultado de validación
   */
  getChatroomMessagesPaginated(friendId, page = 0, pageSize = 50) {
    const errors = [];
    
    // Validar friendId
    const friendErrors = this.getChatroomMessages(friendId);
    errors.push(...friendErrors.errors);
    
    // Validar page
    if (typeof page !== 'number') {
      errors.push('page debe ser un número');
    } else if (page < 0) {
      errors.push('page debe ser mayor o igual a 0');
    } else if (!Number.isInteger(page)) {
      errors.push('page debe ser un número entero');
    }
    
    // Validar pageSize
    if (typeof pageSize !== 'number') {
      errors.push('pageSize debe ser un número');
    } else if (pageSize <= 0) {
      errors.push('pageSize debe ser mayor a 0');
    } else if (pageSize > 100) {
      errors.push('pageSize no puede exceder 100');
    } else if (!Number.isInteger(pageSize)) {
      errors.push('pageSize debe ser un número entero');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

/**
 * Validador genérico para cualquier tipo de datos.
 */
export class Validator {
  constructor() {
    this.errors = [];
  }

  /**
   * Agrega una validación personalizada.
   * @param {Function} validationFn - Función de validación
   * @param {string} errorMessage - Mensaje de error
   * @returns {Validator} Instancia actual para chaining
   */
  custom(validationFn, errorMessage) {
    try {
      const result = validationFn();
      if (!result) {
        this.errors.push(errorMessage);
      }
    } catch (error) {
      this.errors.push(`Error en validación: ${error.message}`);
    }
    
    return this;
  }

  /**
   * Valida que un campo no esté vacío.
   * @param {any} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @returns {Validator} Instancia actual para chaining
   */
  required(value, fieldName) {
    const errors = validationHelpers.validateNonEmptyString(value, fieldName);
    this.errors.push(...errors);
    return this;
  }

  /**
   * Valida longitud de un string.
   * @param {string} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @param {number} minLength - Longitud mínima
   * @param {number} maxLength - Longitud máxima
   * @returns {Validator} Instancia actual para chaining
   */
  length(value, fieldName, minLength = 0, maxLength = Infinity) {
    const errors = validationHelpers.validateStringLength(value, fieldName, minLength, maxLength);
    this.errors.push(...errors);
    return this;
  }

  /**
   * Valida formato UUID.
   * @param {string} value - Valor a validar
   * @param {string} fieldName - Nombre del campo
   * @returns {Validator} Instancia actual para chaining
   */
  uuid(value, fieldName) {
    const errors = validationHelpers.validateUUID(value, fieldName);
    this.errors.push(...errors);
    return this;
  }

  /**
   * Obtiene el resultado de la validación.
   * @returns {Object} Resultado de validación
   */
  getResult() {
    return {
      isValid: this.errors.length === 0,
      errors: [...this.errors]
    };
  }

  /**
   * Lanza error si hay validaciones fallidas.
   * @throws {Error} Error con todos los mensajes de validación
   */
  throwIfInvalid() {
    const result = this.getResult();
    if (!result.isValid) {
      throw new Error(result.errors.join(', '));
    }
    return result;
  }

  /**
   * Resetea las validaciones.
   * @returns {Validator} Instancia actual para chaining
   */
  reset() {
    this.errors = [];
    return this;
  }
}

/**
 * Función de conveniencia para crear un validador.
 * @returns {Validator} Nueva instancia de Validator
 */
export const createValidator = () => new Validator();

// Exportar helpers para uso en otros módulos
export { validationHelpers };
